import { useEffect, useState } from 'react'
import { auroraStore } from '../../store/auroraStore'
import type { PlanBlock } from '../../store/selectors'
import { setBlockStatus } from '../../lib/planEdit'

/**
 * The Done and Notes columns of the paper checksheet, on one block.
 *
 * Three small marks — done, partly, skipped — and a note that appears only
 * when asked for or already written. The controls whisper: the card belongs
 * to the activity, not to its bookkeeping.
 */
export function BlockCheck({ block, date }: { block: PlanBlock; date: string }) {
  const [note, setNote] = useState(block.note ?? '')
  const [noteOpen, setNoteOpen] = useState(false)
  useEffect(() => setNote(block.note ?? ''), [block.note])

  const mark = (status: 'done' | 'partial' | 'skipped') => {
    const next = block.status === status ? 'pending' : status
    setBlockStatus(date, block.id, next, block.title)
  }

  const commit = () => {
    if ((block.note ?? '') === note.trim()) return
    ;(auroraStore.getState() as any).setBlockNote(block.id, note)
  }

  const showNote = noteOpen || !!block.note

  return (
    <div className="check">
      <div className="check-row">
        <div className="check-marks" role="group" aria-label={`How did "${block.title}" go`}>
          <Mark active={block.status === 'done'} kind="done" onClick={() => mark('done')} label="Done" />
          <Mark active={block.status === 'partial'} kind="partial" onClick={() => mark('partial')} label="Partly" />
          <Mark active={block.status === 'skipped'} kind="skipped" onClick={() => mark('skipped')} label="Skipped" />
        </div>
        {!showNote && (
          <button type="button" className="check-note-add" onClick={() => setNoteOpen(true)}>
            Add a note
          </button>
        )}
      </div>
      {showNote && (
        <input
          className="check-note"
          value={note}
          placeholder="What happened, in a line…"
          autoFocus={noteOpen && !block.note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => { commit(); if (!note.trim()) setNoteOpen(false) }}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
          aria-label={`Notes for ${block.title}`}
        />
      )}
    </div>
  )
}

function Mark({ active, kind, onClick, label }: { active: boolean; kind: string; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      className={`check-mark check-mark-${kind}${active ? ' is-active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      title={label}
    >
      {kind === 'done' && <CheckIcon />}
      {kind === 'partial' && <HalfIcon />}
      {kind === 'skipped' && <SkipIcon />}
      <span className={`check-mark-label${active ? '' : ' sr-only'}`}>{label}</span>
    </button>
  )
}

const S = {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.4,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true,
}
const CheckIcon = () => <svg {...S} width="13" height="13"><path d="M4 12.5l5 5L20 6.5" /></svg>
const SkipIcon = () => <svg {...S} width="11" height="11"><path d="M6 6l12 12M18 6L6 18" /></svg>
const HalfIcon = () => (
  <svg {...S} width="13" height="13" strokeWidth={2}>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" stroke="none" />
  </svg>
)
